import { useTranslation } from "react-i18next";
import { Check, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * «SUGERENCIA DE LA IA · ARAÑA ROJA · 82 %» — la pastilla del triaje (plan cultivos SIEX
 * F7, 05-10-2026; decisión D-triaje: pastilla, no campo preseleccionado, durante la
 * medición).
 *
 * Dice SIEMPRE que es de la IA (F6 de sanidad-focos: lo que propone la IA se dice que es
 * suyo) y no hace nada sola: un toque elige esa plaga en el desplegable, igual que si la
 * eligiera la persona, y el foco lo propone después la regla de siempre (F1: nunca hay un
 * enlace que quien confirma no haya tenido delante). Si la plaga ya está elegida, se ve
 * marcada y no se puede volver a pulsar.
 */
export function AiSuggestionChip({
  pestName,
  confidence,
  outsideList = false,
  picked,
  onPick,
  disabled = false,
  className,
}: {
  pestName: string;
  /** 0–1, o null si no viene. */
  confidence: number | null;
  outsideList?: boolean;
  picked: boolean;
  onPick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const pct = typeof confidence === "number" && Number.isFinite(confidence) ? Math.round(confidence * 100) : null;
  return (
    <div className={cn("space-y-1", className)} data-testid="ai-suggestion">
      {/* Dos renglones para que quepa en un teléfono de 375 px: arriba de quién es y con qué
          seguridad («Sugerencia de la IA · 82 %»), abajo la plaga y «Elegir». En un renglón, el
          nombre se quedaba a cero de ancho y la etiqueta «Elegida» se salía del borde. */}
      <button
        type="button"
        onClick={onPick}
        disabled={disabled || picked}
        aria-pressed={picked}
        aria-label={t("pests.scouting.aiSuggestion.pickAria", { pest: pestName, defaultValue: "Elegir la sugerencia de la IA: {{pest}}" })}
        data-testid="ai-suggestion-chip"
        data-picked={picked ? "true" : "false"}
        className={cn(
          "flex w-full min-w-0 flex-col gap-0.5 rounded-2xl border px-3 py-2 text-left shadow-sm transition-transform active:scale-[0.99]",
          picked
            ? "border-[#7c5ccf] bg-[#f3effc] text-[#3b2a73]"
            : "border-[#d9cff3] bg-white text-[#0c1f3d] hover:bg-[#faf8fe]",
          "disabled:active:scale-100",
        )}
      >
        <span className="flex w-full min-w-0 items-center gap-1.5 text-[11px] font-semibold text-[#5b44a8]">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-[#7c5ccf]" aria-hidden />
          <span className="min-w-0 truncate">{t("pests.scouting.aiSuggestion.label", "Sugerencia de la IA")}</span>
          {pct != null && (
            <>
              <span className="shrink-0 text-[#a99bd8]" aria-hidden>·</span>
              <span className="shrink-0 tabular-nums" data-testid="ai-suggestion-confidence">{pct} %</span>
            </>
          )}
        </span>
        <span className="flex w-full min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-bold" data-testid="ai-suggestion-pest">{pestName}</span>
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold",
              picked ? "bg-[#7c5ccf] text-white" : "bg-[#efeafb] text-[#5b44a8]",
            )}
          >
            {picked && <Check className="h-3 w-3" aria-hidden />}
            {picked ? t("pests.scouting.aiSuggestion.picked", "Elegida") : t("pests.scouting.aiSuggestion.pick", "Elegir")}
          </span>
        </span>
      </button>
      <p className="px-1 text-[11px] leading-snug text-muted-foreground">
        {t("pests.scouting.aiSuggestion.hint", "Lo propone la IA mirando la foto y las plagas de este cultivo: compruébalo antes de confirmar.")}
        {outsideList && (
          <span className="ml-1 font-semibold text-amber-700" data-testid="ai-suggestion-outside">
            ({t("pests.scouting.aiSuggestion.outsideList", "fuera de la lista del cultivo")})
          </span>
        )}
      </p>
    </div>
  );
}

export default AiSuggestionChip;
